// src/pages/EventReminderSettingsPage.js
// Updated: 08/22/2025 08:41 PM

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  Container, Typography, Paper, Button,
  CircularProgress, Alert, FormGroup, FormControlLabel, Checkbox
} from '@mui/material';

const reminderOptions = [
  { label: '1 Hour Before', value: 60 },
  { label: '3 Hours Before', value: 180 },
  { label: '24 Hours Before', value: 1440 },
  { label: '2 Days Before', value: 2880 },
  { label: '1 Week Before', value: 10080 },
];

function EventReminderSettingsPage() {
  const { userProfile, churchProfile } = useAuth();
  const [reminderIntervals, setReminderIntervals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userProfile?.churchId) {
      setLoading(false);
      return;
    }
    const settingsRef = doc(db, `churches/${userProfile.churchId}/configurations/eventReminderSettings`);
    getDoc(settingsRef).then((docSnap) => {
      if (docSnap.exists()) {
        setReminderIntervals(docSnap.data().reminderIntervals || []);
      }
    }).catch((err) => {
      console.error("Error fetching reminder settings:", err);
      setError("Could not load existing settings.");
    }).finally(() => {
      setLoading(false);
    });
  }, [userProfile?.churchId]);

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    const intervalValue = parseInt(value, 10);
    setReminderIntervals((prev) =>
      checked ? [...prev, intervalValue] : prev.filter((v) => v !== intervalValue)
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const settingsRef = doc(db, `churches/${userProfile.churchId}/configurations/eventReminderSettings`);
      await setDoc(settingsRef, { reminderIntervals }, { merge: true });
      setSuccess('Settings saved successfully!');
    } catch (err) {
      console.error("Error saving settings:", err);
      setError(`Failed to save settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!churchProfile?.enableEventReminders) {
    return (
        <Container>
            <Alert severity="warning">
                Event Reminders are not enabled for your church. A Super Admin can enable this in Church Management.
            </Alert>
        </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: { xs: 2, md: 4 }, mt: 3 }}>
        <Typography variant="h4" gutterBottom>Event Reminder Settings</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Choose when automated push notification reminders should be sent for upcoming events.
        </Typography>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormGroup>
          {reminderOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={reminderIntervals.includes(option.value)}
                  onChange={handleCheckboxChange}
                  value={option.value}
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>

        <Button
          variant="contained"
          size="large"
          disabled={saving}
          onClick={handleSave}
          sx={{ mt: 3 }}
        >
          {saving ? <CircularProgress size={24} /> : 'Save Settings'}
        </Button>
      </Paper>
    </Container>
  );
}

export default EventReminderSettingsPage;